/* ============================================================
   VITALCASA CRM — ThemeProvider
   Sistema global de temas claro/oscuro.
   ============================================================ */

import { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

// ============================================================
// TEMA CLARO  (paleta app móvil Vitalcasa)
// ============================================================
const lightTheme = {
  name: 'light',
  colors: {
    // Fondos — fondo más oscuro para que las cards blancas destaquen bien
    bgApp:          '#b8b8b0',   // gris cálido medio-oscuro — contraste claro con cards blancas
    bgPanel:        '#a8a8a0',   // nav/panel: un escalón más oscuro que el fondo
    bgCard:         '#ffffff',   // cards blancas puras
    bgCardHover:    '#f5f5f5',
    bgInput:        '#ffffff',   // inputs blancos puros
    bgHeader:       '#1a3a5c',   // header: marino (marca)
    bgHeaderText:   '#ffffff',

    // Textos
    textPrimary:    '#000000',   // negro puro (= --tx app móvil)
    textSecondary:  '#1a3a5c',   // marino oscuro para énfasis secundario
    textMuted:      '#6b7280',   // gris (= --sub app móvil)
    textPlaceholder:'#9ca3af',   // gris suave (= --mut app móvil)

    // Bordes — gris cálido, no azulado
    border:         '#c8c8be',   // borde cálido (= --brd app móvil)
    inputBg:        '#ffffff',   // inputs blancos
    inputBorder:    '#c8c8be',   // borde cálido 2px
    borderLight:    '#e5e7eb',   // borde muy suave para divisores
    borderFocus:    '#3b82f6',   // mismo azul que Historial «Todos» / precaptación

    // Acentos — #3b82f6 (chip Historial «Todos»), no el marino #1a3a5c
    accentBlue:      '#3b82f6',
    accentBlueDark:  '#2563eb',
    accentBlueLight: '#eff6ff',
    accentBlueBorder:'#93c5fd',
    accentGreen:     '#22c55e',   // verde (= --grn app móvil)
    accentGreenDark: '#16a34a',
    accentGreenLight:'#f0fdf4',
    accentGreenBorder:'#bbf7d0',
    accentPink:      '#ec4899',
    accentPinkLight: '#fdf2f8',
    accentPurple:    '#7c3aed',
    accentPurpleDark:'#6d28d9',
    accentOrange:    '#d97706',
    accentRed:       '#ef4444',   // rojo (= --red app móvil)

    // Chips — beige cálido, igual que botones inactivos de la app
    chipBg:          '#e8e8e0',   // beige cálido
    chipText:        '#000000',
    chipBorder:      '#c8c8be',
    chipHoverBg:     '#d8d8d0',
    chipHoverText:   '#3b82f6',
    chipHoverBorder: '#3b82f6',
    chipActiveBg:    '#3b82f6',
    chipActiveText:  '#ffffff',
    chipActiveBorder:'transparent',

    // Etapas
    etapaActiveBg:      'linear-gradient(135deg, #3b82f6, #2563eb)',
    etapaActiveText:    '#ffffff',
    etapaActiveShadow:  '0 2px 8px rgba(59,130,246,0.30)',
    etapaInactiveBg:    '#e8e8e0',
    etapaInactiveText:  '#000000',
    etapaInactiveBorder:'#c8c8be',

    // Labels
    labelColor:     '#16a34a',

    // Tarjetas de propiedades
    propCardBg:          '#ffffff',
    propCardBorder:      '#e5e7eb',
    propCardShadow:      '0 1px 4px rgba(0,0,0,0.08)',
    propCardHoverShadow: '0 6px 20px rgba(0,0,0,0.14)',
    propBadgeBg:         'rgba(0,0,0,0.65)',
    propBadgeText:       '#ffffff',
    propExtraBg:         '#eff6ff',
    propExtraText:       '#3b82f6',

    // Secciones
    sectionBorder:  '#c8c8be',
    cardShadow:     '0 1px 4px rgba(0,0,0,0.08), 0 4px 12px rgba(0,0,0,0.06)',

    // Botones de acción
    btnCallBg:        'linear-gradient(135deg, #fde047, #eab308)',
    btnCallShadow:    '0 2px 6px rgba(234,179,8,0.35)',
    btnCallIconStroke:'#dc2626',
    btnWhatsappBg:    'linear-gradient(135deg, #25d366, #128c7e)',
    btnWhatsappShadow:'0 2px 6px rgba(37,211,102,0.3)',
    btnCitaBg:        'linear-gradient(135deg, #8b5cf6, #7c3aed)',
    btnCitaShadow:    '0 2px 6px rgba(139,92,246,0.3)',
    btnNotaBg:        'linear-gradient(135deg, #6b7280, #4b5563)',
    btnNotaShadow:    '0 2px 6px rgba(107,114,128,0.3)',
    btnActionBg:      '#e8e8e0',   // beige cálido (= btn-secondary app móvil)
    btnActionBorder:  '#c8c8be',
    btnActionText:    '#000000',

    // Scrollbar
    scrollThumb:    '#c8c8be',
    scrollTrack:    'transparent',

    // Demandas
    demandaActiveBorder: '#3b82f6',
    demandaActiveBg:     '#eff6ff',
    demandaInactiveBorder:'#e5e7eb',
    demandaInactiveBg:   '#ffffff',

    // Indicador en vivo
    liveColor:      '#22c55e',

    // Overlay
    overlayBg:      'rgba(0,0,0,0.50)',
  },
};

// ============================================================
// TEMA OSCURO
// ============================================================
const darkTheme = {
  name: 'dark',
  colors: {
    // Fondos
    bgApp:       '#08090d',
    bgPanel:     '#101318',
    bgCard:      '#181c24',
    bgCardHover: '#1f2530',
    bgInput:     '#0d1117',   // inputs: casi negro azulado — distinto del bgCard #181c24
    bgHeader:    '#08090d',
    bgHeaderText:'#e2e8f0',

    // Textos
    textPrimary:    '#f1f5f9',
    textSecondary:  '#c8d2e0',
    textMuted:      '#94a3b8',
    textPlaceholder:'#5a6a7e',   // placeholder más visible en oscuro

    // Bordes
    border:      '#3a4358',
    inputBg:     '#0a0e16',   // casi negro puro — más oscuro que la card
    inputBorder: '#4a6080',   // borde azulado visible en oscuro
    borderLight: '#2a3244',
    borderFocus: '#2563eb',

    // Acentos
    accentBlue:      '#2563eb',
    accentBlueDark:  '#1d4ed8',
    accentBlueLight: '#172040',
    accentBlueBorder:'#1e3a5f',
    accentGreen:     '#22c55e',
    accentGreenDark: '#16a34a',
    accentGreenLight:'#0f1f16',
    accentGreenBorder:'#14532d',
    accentPink:      '#ec4899',
    accentPinkLight: '#2a1220',
    accentPurple:    '#8b5cf6',
    accentPurpleDark:'#7c3aed',
    accentOrange:    '#f59e0b',
    accentRed:       '#ef4444',

    // Chips
    chipBg:          '#181c24',
    chipText:        '#c8d2e0',
    chipBorder:      '#3a4358',
    chipHoverBg:     '#172040',
    chipHoverText:   '#60a5fa',
    chipHoverBorder: '#2563eb',
    chipActiveBg:    '#1d4ed8',
    chipActiveText:  '#ffffff',
    chipActiveBorder:'transparent',

    // Etapas
    etapaActiveBg:      'linear-gradient(135deg, #2563eb, #1d4ed8)',
    etapaActiveText:    '#ffffff',
    etapaActiveShadow:  '0 2px 8px rgba(37,99,235,0.35)',
    etapaInactiveBg:    '#181c24',
    etapaInactiveText:  '#c8d2e0',
    etapaInactiveBorder:'#3a4358',

    // Labels
    labelColor: '#34d399',

    // Tarjetas de propiedades
    propCardBg:          '#181c24',
    propCardBorder:      '#3a4358',
    propCardShadow:      '0 1px 3px rgba(0,0,0,0.3)',
    propCardHoverShadow: '0 8px 25px rgba(0,0,0,0.5)',
    propBadgeBg:         'rgba(0,0,0,0.85)',
    propBadgeText:       '#ffffff',
    propExtraBg:         '#172040',
    propExtraText:       '#60a5fa',

    // Secciones
    sectionBorder: '#3a4358',
    cardShadow:    'none',

    // Botones de acción
    btnCallBg:        'linear-gradient(135deg, #fde047, #eab308)',
    btnCallShadow:    '0 2px 6px rgba(234,179,8,0.35)',
    btnCallIconStroke:'#dc2626',
    btnWhatsappBg:    'linear-gradient(135deg, #25d366, #128c7e)',
    btnWhatsappShadow:'0 2px 6px rgba(37,211,102,0.3)',
    btnCitaBg:        'linear-gradient(135deg, #8b5cf6, #7c3aed)',
    btnCitaShadow:    '0 2px 6px rgba(139,92,246,0.3)',
    btnNotaBg:        'linear-gradient(135deg, #6b7280, #4b5563)',
    btnNotaShadow:    '0 2px 6px rgba(107,114,128,0.3)',
    btnActionBg:      '#181c24',
    btnActionBorder:  '#3a4358',
    btnActionText:    '#c8d2e0',

    // Scrollbar
    scrollThumb: '#4a5568',
    scrollTrack: 'transparent',

    // Demandas
    demandaActiveBorder: '#2563eb',
    demandaActiveBg:     '#172040',
    demandaInactiveBorder:'#3a4358',
    demandaInactiveBg:   '#181c24',

    // Indicador en vivo
    liveColor: '#22c55e',

    // Overlay
    overlayBg: 'rgba(0,0,0,0.85)',
  },
};

// ============================================================
// PROVIDER
// ============================================================
export function ThemeProvider({ children }) {
  const [themeName, setThemeName] = useState(() => {
    try {
      return localStorage.getItem('vitalcasa-theme') || 'light';
    } catch {
      return 'light';
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem('vitalcasa-theme', themeName);
      document.body.classList.remove('theme-light', 'theme-dark');
      document.body.classList.add('theme-' + themeName);
    } catch {}
  }, [themeName]);

  const theme = themeName === 'dark' ? darkTheme : lightTheme;
  const toggleTheme = () => setThemeName(p => p === 'light' ? 'dark' : 'light');
  const setTheme = (name) => setThemeName(name);

  return (
    <ThemeContext.Provider value={{ theme, themeName, toggleTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme debe usarse dentro de ThemeProvider');
  return ctx;
}
